import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  CalendarCheck, LayoutDashboard, CalendarRange, CalendarDays, Columns3, CalendarClock,
  MessageCircle, Printer, Settings, Menu, X, ChevronsLeft, ChevronsRight, ChevronDown, ChevronRight,
  Search, GanttChartSquare, Sun, Moon, Activity, BarChart3, Monitor, History, Sparkles, Cpu,
} from 'lucide-react';
import { fetchDashboard } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { AREE } from '../../constants/areas';
import { formatSettimanaConRange } from '../../lib/weekFormat';
import Logo, { LogoIcon } from './Logo';
import MiniCalendar from './MiniCalendar';
import Tooltip from './Tooltip';
import SavedViews from './SavedViews';
import AvvisiPanel from './AvvisiPanel';
import InviteFriendButton from './InviteFriendButton';
import StreakWidget from './StreakWidget';
import { useTheme } from '../../lib/useTheme';

const MONTH_LABELS_IT = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];

function pad(n) { return String(n).padStart(2, '0'); }
function currentContext() {
  const d = new Date();
  const anno = d.getFullYear();
  const mese = `${anno}-${pad(d.getMonth() + 1)}`;
  const quarter = `Q${Math.floor(d.getMonth() / 3) + 1}-${anno}`;
  const tmp = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = tmp.getUTCDay() || 7;
  tmp.setUTCDate(tmp.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((tmp - yearStart) / 86400000 + 1) / 7);
  const settimana = `${tmp.getUTCFullYear()}-W${pad(week)}`;
  const giorno = `${anno}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  return { anno, quarter, mese, settimana, giorno };
}

export default function Sidebar({ onOpenCoach }) {
  const loc = useLocation();
  const { signOut, user } = useAuth();
  const { theme, isDark, isSystem, cycle: cycleTheme } = useTheme();
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('qp.sidebar.collapsed') === '1'; } catch { return false; }
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState(() => {
    try {
      const raw = localStorage.getItem('qp.sidebar.openGroups');
      return raw ? JSON.parse(raw) : { tempi: true, obiettivi: true, pianifica: true };
    } catch { return { tempi: true, obiettivi: true, pianifica: true }; }
  });
  const [obiettivi, setObiettivi] = useState([]);

  const ctx = currentContext();

  useEffect(() => {
    fetchDashboard()
      .then((d) => setObiettivi(d.obiettivi || []))
      .catch((err) => {
        // Errore non bloccante (la sidebar mostra solo nav); logghiamo per debug.
        console.warn('Sidebar: fetchDashboard fallito', err?.message || err);
      });
  }, []);

  // Chiudi automaticamente la mobile drawer al cambio rotta
  useEffect(() => {
    setMobileOpen(false);
  }, [loc.pathname]);

  const toggle = () => {
    setCollapsed((c) => {
      const next = !c;
      try { localStorage.setItem('qp.sidebar.collapsed', next ? '1' : '0'); }
      catch (e) { console.warn('Sidebar: localStorage collapsed non scrivibile', e?.message || e); }
      return next;
    });
  };
  const toggleGroup = (k) => {
    setOpenGroups((g) => {
      const next = { ...g, [k]: !g[k] };
      try { localStorage.setItem('qp.sidebar.openGroups', JSON.stringify(next)); }
      catch (e) { console.warn('Sidebar: localStorage openGroups non scrivibile', e?.message || e); }
      return next;
    });
  };

  const isActive = (path) => {
    if (path === '/' && loc.pathname === '/') return true;
    if (path !== '/' && loc.pathname.startsWith(path)) return true;
    return false;
  };

  const width = collapsed ? 'w-[60px]' : 'w-[240px]';

  return (
    <>
      {/* Mobile menu button */}
      <button
        onClick={() => setMobileOpen(true)}
        className="lg:hidden fixed top-3 left-3 z-40 p-2 bg-canvas border border-line hover:border-ink transition-colors"
        data-testid="sidebar-mobile-open"
        aria-label="Apri menu"
      >
        <Menu size={18} strokeWidth={1.5} />
      </button>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/40 z-40"
          onClick={() => setMobileOpen(false)}
          data-testid="sidebar-mobile-overlay"
        />
      )}

      <aside
        className={
          'bg-canvas border-r border-line h-screen sticky top-0 flex flex-col transition-[width] duration-200 z-50 ' +
          width + ' ' +
          'fixed lg:sticky ' +
          (mobileOpen ? 'left-0' : '-left-[260px] lg:left-0')
        }
        data-testid="sidebar"
        data-collapsed={collapsed ? '1' : '0'}
      >
        {/* Header brand */}
        <div className="border-b border-line">
          {collapsed ? (
            <div className="px-1 py-4 flex flex-col items-center gap-2">
              <LogoIcon size={26} />
              <button
                onClick={toggle}
                className="p-1.5 text-soft hover:text-ink hover:bg-linesoft transition-colors hidden lg:inline-flex"
                data-testid="sidebar-toggle"
                title="Espandi"
                aria-label="Espandi sidebar"
              >
                <ChevronsRight size={14} strokeWidth={1.5} />
              </button>
            </div>
          ) : (
            <div className="px-3 py-4 flex items-center gap-2">
              <Logo size={22} />
              <span className="flex-1" />
              <button
                onClick={toggle}
                className="p-1.5 text-soft hover:text-ink hover:bg-linesoft transition-colors hidden lg:inline-flex shrink-0"
                data-testid="sidebar-toggle"
                title="Riduci"
                aria-label="Riduci sidebar"
              >
                <ChevronsLeft size={14} strokeWidth={1.5} />
              </button>
              <button
                onClick={() => setMobileOpen(false)}
                className="p-1.5 text-soft hover:text-ink lg:hidden shrink-0"
                data-testid="sidebar-mobile-close"
                aria-label="Chiudi menu"
              >
                <X size={14} strokeWidth={1.5} />
              </button>
            </div>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-1">
          <Item to="/oggi" icon={<CalendarCheck size={16} strokeWidth={1.5} />} label="Oggi" collapsed={collapsed} active={isActive('/oggi')} testid="nav-oggi" accent />
          <Item to="/" icon={<LayoutDashboard size={16} strokeWidth={1.5} />} label="Dashboard" collapsed={collapsed} active={loc.pathname === '/'} testid="nav-dashboard" />
          <Item to="/timeline" icon={<GanttChartSquare size={16} strokeWidth={1.5} />} label="Timeline" collapsed={collapsed} active={isActive('/timeline')} testid="nav-timeline" />
          <Item to="/calendario" icon={<CalendarDays size={16} strokeWidth={1.5} />} label="Calendario" collapsed={collapsed} active={isActive('/calendario')} testid="nav-calendario" />
          <Item to="/heatmap" icon={<Activity size={16} strokeWidth={1.5} />} label="Heatmap" collapsed={collapsed} active={isActive('/heatmap')} testid="nav-heatmap" />
          <Item to="/stats" icon={<BarChart3 size={16} strokeWidth={1.5} />} label="Statistiche" collapsed={collapsed} active={isActive('/stats')} testid="nav-stats" />
          <Item to="/coach-history" icon={<History size={16} strokeWidth={1.5} />} label="Storico Coach" collapsed={collapsed} active={isActive('/coach-history')} testid="nav-coach-history" />
          <Item to="/consumi-ai" icon={<Cpu size={16} strokeWidth={1.5} />} label="Consumi AI" collapsed={collapsed} active={isActive('/consumi-ai')} testid="nav-consumi-ai" />

          {/* Gruppo Tempi — CONSULTAZIONE (viste dati /periodo/*) */}
          <Group title="Consulta · periodi" open={openGroups.tempi} onToggle={() => toggleGroup('tempi')} collapsed={collapsed} testid="nav-group-tempi">
            <Item to={`/periodo/quarter/${ctx.quarter}`} icon={<CalendarRange size={14} strokeWidth={1.5} />} label={`Quarter · ${ctx.quarter.split('-')[0]}`} collapsed={collapsed} sub active={loc.pathname.startsWith('/periodo/quarter')} testid="nav-quarter" />
            <Item to={`/periodo/mese/${ctx.mese}`} icon={<CalendarDays size={14} strokeWidth={1.5} />} label={`Mese · ${MONTH_LABELS_IT[parseInt(ctx.mese.split('-')[1], 10) - 1]}`} collapsed={collapsed} sub active={loc.pathname.startsWith('/periodo/mese')} testid="nav-mese" />
            <Item to={`/periodo/settimana/${ctx.settimana}`} icon={<Columns3 size={14} strokeWidth={1.5} />} label={`Settimana · ${formatSettimanaConRange(ctx.settimana)}`} collapsed={collapsed} sub active={loc.pathname.startsWith('/periodo/settimana')} testid="nav-settimana" />
            <Item to={`/periodo/giorno/${ctx.giorno}`} icon={<CalendarClock size={14} strokeWidth={1.5} />} label={`Giorno · ${ctx.giorno.slice(8)}/${ctx.giorno.slice(5,7)}`} collapsed={collapsed} sub active={loc.pathname.startsWith('/periodo/giorno')} testid="nav-giorno" />
          </Group>

          {/* Gruppo Pianifica — chat guidate col coach (distinte dalle viste dati) */}
          <Group title="Pianifica col coach" open={openGroups.pianifica} onToggle={() => toggleGroup('pianifica')} collapsed={collapsed} testid="nav-group-pianifica">
            <Item to="/setup" icon={<Sparkles size={14} strokeWidth={1.5} />} label="Setup anno" collapsed={collapsed} sub active={loc.pathname.startsWith('/setup')} testid="nav-chat-setup" />
            <Item to="/trimestre" icon={<MessageCircle size={14} strokeWidth={1.5} />} label="Pianifica trimestre" collapsed={collapsed} sub active={loc.pathname.startsWith('/trimestre')} testid="nav-chat-trimestre" />
            <Item to="/mese" icon={<MessageCircle size={14} strokeWidth={1.5} />} label="Pianifica mese" collapsed={collapsed} sub active={loc.pathname === '/mese' || loc.pathname.startsWith('/mese/')} testid="nav-chat-mese" />
            <Item to="/pianifica-settimana" icon={<MessageCircle size={14} strokeWidth={1.5} />} label="Pianifica settimana" collapsed={collapsed} sub active={loc.pathname.startsWith('/pianifica-settimana')} testid="nav-chat-pianifica-settimana" />
            <Item to="/settimana" icon={<MessageCircle size={14} strokeWidth={1.5} />} label="Review settimana" collapsed={collapsed} sub active={loc.pathname === '/settimana' || loc.pathname.startsWith('/settimana/')} testid="nav-chat-settimana" />
          </Group>

          {/* Gruppo Obiettivi */}
          <Group title={`Obiettivi · ${obiettivi.length}`} open={openGroups.obiettivi} onToggle={() => toggleGroup('obiettivi')} collapsed={collapsed} testid="nav-group-obiettivi">
            {obiettivi.length === 0 && !collapsed && (
              <p className="px-3 py-2 font-body text-[11px] text-mute italic">Nessun obiettivo</p>
            )}
            {obiettivi.map((o) => {
              const meta = AREE[(o.area || '').toLowerCase()] || { color: 'var(--qp-text-soft)' };
              return (
                <Item
                  key={o.id}
                  to={`/obiettivo/${o.id}`}
                  icon={<span className="inline-block w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: meta.color }} />}
                  label={o.titolo}
                  collapsed={collapsed}
                  sub
                  active={loc.pathname === `/obiettivo/${o.id}`}
                  testid={`nav-obiettivo-${o.id}`}
                  monoOrdine={pad(o.ordine)}
                />
              );
            })}
          </Group>

          {/* Viste salvate (bookmark filtri) */}
          <SavedViews collapsed={collapsed} />

          {/* Avvisi non invasivi (review dovuta, KR a rischio, ecc.) */}
          {!collapsed && <AvvisiPanel variant="sidebar" />}

          {/* Cerca ⌘K */}
          <Tooltip label="Cerca (⌘K)" disabled={!collapsed}>
            <button
              onClick={() => {
                // Trigger the same shortcut programmatically
                window.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true }));
              }}
              className={
                'group w-full flex items-center gap-3 px-3 py-2 text-left transition-colors hover:bg-linesoft mt-3 ' +
                'border border-line hover:border-ink text-ink'
              }
              data-testid="nav-cmdk"
            >
              <Search size={16} strokeWidth={1.5} className="shrink-0" />
              {!collapsed && (
                <>
                  <span className="flex-1 font-mono text-[11px] uppercase tracking-[0.18em]">Cerca</span>
                  <kbd className="font-mono text-[9px] uppercase tracking-[0.15em] text-mute border border-line px-1 py-0.5">⌘K</kbd>
                </>
              )}
            </button>
          </Tooltip>

          {/* Parla col coach */}
          <Tooltip label="Parla col coach" disabled={!collapsed}>
            <button
              onClick={onOpenCoach}
              className={
                'group w-full flex items-center gap-3 px-3 py-2 text-left transition-colors hover:bg-linesoft mt-3 ' +
                'border border-dashed border-line hover:border-accent text-ink'
              }
              data-testid="nav-coach"
            >
              <MessageCircle size={16} strokeWidth={1.5} className="text-accent shrink-0" />
              {!collapsed && (
                <span className="font-mono text-[11px] uppercase tracking-[0.18em]">Parla col coach</span>
              )}
            </button>
          </Tooltip>
        </nav>

        {/* Mini calendar — solo quando espansa */}
        {!collapsed && <MiniCalendar />}

        {/* Footer */}
        <div className="px-2 py-3 border-t border-line space-y-1">
          <Item to="/stampa" icon={<Printer size={16} strokeWidth={1.5} />} label="Stampa · PDF" collapsed={collapsed} active={isActive('/stampa')} testid="nav-stampa" />
          <Item to="/legenda" icon={<Settings size={16} strokeWidth={1.5} />} label="Legenda · Impostazioni" collapsed={collapsed} active={isActive('/legenda')} testid="nav-legenda" />

          {/* Theme toggle ciclico: system → light → dark → system */}
          <Tooltip
            label={isSystem ? 'Tema: sistema' : isDark ? 'Tema: scuro' : 'Tema: chiaro'}
            disabled={!collapsed}
          >
            <button
              onClick={cycleTheme}
              className={
                'group w-full flex items-center gap-3 px-3 py-2 text-left transition-colors hover:bg-linesoft text-soft hover:text-ink'
              }
              data-testid="nav-theme-toggle"
              data-theme={theme}
              aria-label={`Cicla tema (corrente: ${theme}). Sistema → chiaro → scuro.`}
            >
              <span className="shrink-0 inline-flex">
                {isSystem ? (
                  <Monitor size={16} strokeWidth={1.5} />
                ) : isDark ? (
                  <Moon size={16} strokeWidth={1.5} />
                ) : (
                  <Sun size={16} strokeWidth={1.5} />
                )}
              </span>
              {!collapsed && (
                <span className="flex-1 truncate font-mono text-[11px] uppercase tracking-[0.18em]">
                  {isSystem ? 'Tema: sistema' : isDark ? 'Tema: scuro' : 'Tema: chiaro'}
                </span>
              )}
            </button>
          </Tooltip>

          {!collapsed && user?.email && (
            <div className="px-3 py-2 mt-2 border-t border-linesoft">
              <div className="font-mono text-[10px] text-mute truncate" title={user.email}>{user.email}</div>
              <button
                onClick={() => signOut()}
                className="mt-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-soft hover:text-accent"
                data-testid="sidebar-logout"
              >
                Esci
              </button>
            </div>
          )}

          <StreakWidget collapsed={collapsed} />
          <InviteFriendButton collapsed={collapsed} />
        </div>
      </aside>
    </>
  );
}

function Item({ to, icon, label, collapsed, active, sub, testid, accent, monoOrdine }) {
  const link = (
    <Link
      to={to}
      className={
        'group flex items-center gap-3 px-3 py-2 text-left transition-colors w-full ' +
        (active
          ? 'bg-ink text-canvas'
          : 'text-soft hover:bg-linesoft hover:text-ink')
      }
      data-testid={testid}
    >
      <span className="shrink-0 inline-flex">{icon}</span>
      {!collapsed && (
        <span className={
          'flex-1 truncate ' +
          (sub
            ? 'font-body text-[12px]'
            : 'font-mono text-[11px] uppercase tracking-[0.18em]')
        }>
          {monoOrdine && <span className="font-mono text-[10px] mr-1.5 opacity-70">{monoOrdine}</span>}
          {label}
        </span>
      )}
      {accent && !collapsed && !active && (
        <span className="inline-block w-1.5 h-1.5 rounded-full bg-accent shrink-0" />
      )}
    </Link>
  );
  return collapsed ? (
    <Tooltip label={label} disabled={!collapsed}>{link}</Tooltip>
  ) : link;
}

function DisabledItem({ icon, label, collapsed, testid }) {
  const node = (
    <div
      className="flex items-center gap-3 px-3 py-2 text-mute cursor-not-allowed w-full"
      data-testid={testid}
    >
      <span className="shrink-0 inline-flex">{icon}</span>
      {!collapsed && (
        <span className="flex-1 truncate font-mono text-[11px] uppercase tracking-[0.18em]">{label}</span>
      )}
    </div>
  );
  return collapsed ? (
    <Tooltip label={label} disabled={!collapsed}>{node}</Tooltip>
  ) : node;
}

function Group({ title, open, onToggle, collapsed, children, testid }) {
  if (collapsed) {
    // Quando ridotta: niente titolo gruppo, solo gli item come icone
    return <div className="space-y-1">{children}</div>;
  }
  return (
    <div className="mt-3" data-testid={testid}>
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-2 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.22em] text-mute hover:text-ink"
        data-testid={`${testid}-toggle`}
        aria-expanded={open}
      >
        {open
          ? <ChevronDown size={11} strokeWidth={1.5} />
          : <ChevronRight size={11} strokeWidth={1.5} />}
        <span className="flex-1 text-left truncate">{title}</span>
      </button>
      {open && <div className="space-y-1">{children}</div>}
    </div>
  );
}

// Stub icona target — non usata

